import { sharedTypes } from '.';
import { APITypes, Dynamo } from '..';

/**
 * The product categories exposed by the API.
 *
 * The `individual-recurring` category is excluded because it is not a valid product category anymore but once was.  Users with this category are automatically "upgraded" to family.
 */
export type APIProductCategories = Exclude<Dynamo.ProductCategories, 'individual-recurring'>;

/**
 * The subscription product categories exposed by the API.
 *
 * The `individual-recurring` category is excluded because it is not a valid product category anymore but once was.  Users with this category are automatically "upgraded" to family.
 */
export type APISubscriptionProductCategories = Exclude<Dynamo.SubscriptionProductCategories, 'individual-recurring'>;

export type APIOneTimePurchaseProductCategories = Dynamo.OneTimePurchaseProductCategories;

export type APIProductTypeIdentifiers = Exclude<
    Dynamo.ProductTypeIdentifiers,
    'individual-plan-monthly' | 'individual-plan-yearly'
>;

export type StripeAppClient = 'mobile' | 'mobile-dev';

export type LegacyWebProductListItem = {
    productId: string;
    priceId: string;
    price: number;
    productType: APITypes.sharedTypes.ProductTypeIdentifiers;
};

export type WebProductListItem = Omit<LegacyWebProductListItem, 'productType'> & {
    productType: APIProductTypeIdentifiers;
};

export type WebProductsResponse = {
    products: WebProductListItem[];
};

export type WebStripeClientType = 'local-web' | 'dev-web' | 'prod-web';

export type WebCheckoutSessionRequest = {
    client: WebStripeClientType;
    productType: APIProductTypeIdentifiers;
};

export type WebCheckoutSessionResponse = {
    url: string;
};

export type WebBillingSessionRequest = {
    client: WebStripeClientType;
};

export type WebBillingSessionResponse = {
    url: string;
};

/**
 * The entitlement exposed by the API.
 *
 * The `Individual Plan` entitlement is excluded because it is not a valid product category anymore but once was.  Users with this category are automatically "upgraded" to family.
 */
export type APIEntitlement = Exclude<sharedTypes.Entitlement, 'Individual Plan'>;

type RegistrationCodeEntitlement = {
    type: 'entitlement';
    durationInMonths: number | null;
    expirationDateTime: string | null;
    code: string;
};

type RegistrationCodeDiscount = {
    type: 'discount';
    discountRate: number;
    code: string;
};

export type PaymentStatusResponse = {
    entitlements: APIEntitlement[];
    products: APIProductTypeIdentifiers[];
    lastPaymentTimestamp: number | null;
    nextPaymentTimestamp: number | null;
    redemptionTimestamp: number | null;
    registrationCode: RegistrationCodeDiscount | RegistrationCodeEntitlement | null;
};

export type ValidateRegistrationCodeRequest = {
    code: string;
};

export type InvalidRegistrationCodeResponse = {
    valid: false;
};

export type ValidDiscountRegistrationCodeResponse = {
    valid: true;
    type: 'discount';
    discountRate: number;
};

export type ValidEntitlementRegistrationCodeResponse = {
    valid: true;
    type: 'entitlement';
    entitlements: APIEntitlement[];
    durationInMonths: number | null;
};

export type ValidateRegistrationCodeResponse =
    | InvalidRegistrationCodeResponse
    | ValidDiscountRegistrationCodeResponse
    | ValidEntitlementRegistrationCodeResponse;

export type ApplyRegistrationCodeRequest = {
    code: string;
};

export type ApplyRegistrationCodeResponse = Record<string, never>;

export type ActiveEntitlementRegistrationCodeStatus = {
    type: 'entitlement';
    entitlements: APIEntitlement[];
    durationInMonths: number | null;
    expirationDateTime: string | null;
    code: string;
    forbidRemoval: boolean;
};

export type ActiveDiscountRegistrationCodeStatus = {
    type: 'discount';
    discountRate: number;
    code: string;
    forbidRemoval: boolean;
};

export type NoRegistrationCodeStatus = {
    type: 'no-registration-code';
};

export type UserRegistrationCodeStatus = {
    status: ActiveEntitlementRegistrationCodeStatus | ActiveDiscountRegistrationCodeStatus | NoRegistrationCodeStatus;
};
